import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import Select from "react-select";
import {
  threeDotsIcon,
  ViewDetailsIcon,
  CopyIcon,
  BlackAddIcon,
  EditProgramIcon,
  ManageCodesIcon,
  PauseProgramIcon,
  DeleteProgramIcon,
} from "../../assets/icons";
import Table from "../../components/Tables";
import PopUpModal from "../../modals/PopUpModal";
import { useDispatch, useSelector } from "react-redux";
import {
  reqToCreateCode,
  reqToGetCodeList,
  reqToGetDiscountVariables,
  reqToGetCodeById,
  reqToUpdateCode,
  reqToUpdateCodeStatus,
  reqToImportCode,
} from "../../redux/slices.js/discountmanagement/discountPromotionSlice";
import PaginationTab from "../../components/PaginationTab";
import { notify } from "../../helpers/commonFunctions";
import useExportCSV from "../../hooks/useCodeExportCSV";
import DatePicker from "react-datepicker";
import { routes } from "../../helpers/constants";

// Extended validation function
const validate = (values) => {
  const errors = {};

  // Basic required fields validation
  if (!values.program) {
    errors.program = "Program is required";
  }
  if (!values.name || values.name < 1) {
    errors.name = "Name is Required";
  }
  if (!values.numberOfCodes || values.numberOfCodes < 1) {
    errors.numberOfCodes = "Number of codes is required and must be at least 1";
  }
  if (!values.channel) {
    errors.channel = "Channel is required";
  }
  if (!values.usesPerCode) {
    errors.usesPerCode = "Uses per code is required";
  }
  if (!values.startDate) {
    errors.startDate = "Start date is required";
  }
  if (!values.endDate) {
    errors.endDate = "End date is required";
  }

  if (!values.discount_scope || values.discount_scope.length === 0) {
    errors.discount_scope = "Discount scope is required";
  }

  // Condition validation - Only validate if conditions are applied
  if (values.is_condition_applied) {
    // Check if at least one condition field is filled
    const hasAnyConditionFilled =
      values.priority ||
      (values.plan_in && values.plan_in.length > 0) ||
      (values.billing_cycle_in && values.billing_cycle_in.length > 0) ||
      values.min_seats ||
      (values.tenant_industry_in && values.tenant_industry_in.length > 0) ||
      (values.geo_in && values.geo_in.length > 0) ||
      (values.trial_state_in && values.trial_state_in.length > 0) ||
      values.min_contract_term_months ||
      (values.discount_stack_groups &&
        values.discount_stack_groups.length > 0) ||
      values.first_purchase_only;

    if (!hasAnyConditionFilled) {
      errors.condition_fields =
        "At least one condition must be filled when conditions are applied";
    }
  }

  return errors;
};

const usesPerCodeOptions = [
  { value: "once", label: "Once per customer" },
  { value: "multiple", label: "Multiple uses" },
  { value: "billing-cycle", label: "Per billing cycle" },
  { value: "unlimited", label: "Unlimited" },
];

// Priority options
const priorityOptions = [
  { value: "low", label: "Low" },
  { value: "medium", label: "Medium" },
  { value: "high", label: "High" },
];

// Plan options (you can fetch these from API if available)
const planOptions = [
  { value: "CORE", label: "Core" },
  { value: "PLUS", label: "Plus" },
  { value: "PREMIUM", label: "Premium" },
  { value: "ENTERPRISE", label: "Enterprise" },
];

// Billing cycle options
const billingCycleOptions = [
  { value: "MONTHLY", label: "Monthly" },
  { value: "QUARTERLY", label: "Quarterly" },
  { value: "ANNUALLY", label: "Annually" },
];

// Tenant industry options
const tenantIndustryOptions = [
  { value: "NFP", label: "Not-for-Profit" },
  { value: "EDU", label: "Education" },
  { value: "TECH", label: "Technology" },
  { value: "HEALTHCARE", label: "Healthcare" },
  { value: "FINANCE", label: "Finance" },
];

// Geography options
const geoOptions = [
  { value: "AU", label: "Australia" },
  { value: "NZ", label: "New Zealand" },
  { value: "US", label: "United States" },
  { value: "UK", label: "United Kingdom" },
  { value: "CA", label: "Canada" },
];

// Trial state options
const trialStateOptions = [
  { value: "NEW_SIGNUP_ONLY", label: "New Signup Only" },
  { value: "TRIAL_ACTIVE", label: "Trial Active" },
  { value: "TRIAL_EXPIRED", label: "Trial Expired" },
  { value: "NO_TRIAL", label: "No Trial" },
];

const CodesTab = ({ openRow, setOpenRow, dropdownRef }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [modalMode, setModalMode] = useState("create");
  const [selectedCodeId, setSelectedCodeId] = useState(null);
  const [selectedCodeData, setSelectedCodeData] = useState(null);
  const [viewDetailsLoading, setViewDetailsLoading] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const { exportToCSV, exportAllToCSV } = useExportCSV();
  const programVariables = useSelector(
    (state) => state.discountCoupons.discount_program_types
  );

  const coupons = useSelector(
    (state) => state?.discountCoupons?.discount?.data
  );

  const codeList = useSelector((state) => state?.discountCoupons?.codelist);
  const totalCount = useSelector(
    (state) => state?.discountCoupons?.codeTotalCount || 0
  );

  const totalPages = Math.ceil(totalCount / pageSize);

  const {
    codelist,
    discount_scope,
    discount_stack_groups,
    discount_channals,
    programNames,
    totalCount: apiTotalCount,
  } = coupons || {};

  useEffect(() => {
    dispatch(
      reqToGetDiscountVariables({
        data: { action: "code" },
        onSuccess: () => {},
        onFailure: (error) => {
          console.error("Error fetching discount variables:", error);
        },
      })
    );
  }, [dispatch]);

  useEffect(() => {
    setLoading(true);
    dispatch(
      reqToGetCodeList({
        data: { page: currentPage, limit: pageSize },
        onSuccess: () => setLoading(false),
        onFailure: () => setLoading(false),
      })
    );
  }, [dispatch, currentPage, pageSize]);

  useEffect(() => {
    setLoading(true);
    dispatch(
      reqToGetCodeList({
        data: { page: currentPage, limit: pageSize },
        onSuccess: (response) => {
          setLoading(false);
          // Set totalCount from API response
          if (response?.totalCount) {
            setTotalCount(response.totalCount);
          } else if (response?.result?.total) {
            setTotalCount(response.result.total);
          } else if (coupons?.totalCount) {
            setTotalCount(coupons.totalCount);
          }
        },
        onFailure: () => setLoading(false),
      })
    );
  }, [dispatch, currentPage, pageSize]);

  const channelOptions = useMemo(() => {
    if (!discount_channals) return [];
    return discount_channals.map((channel) => ({
      value: channel.id,
      label: channel.name,
    }));
  }, [discount_channals]);

  const programOptions = useMemo(() => {
    if (!programNames) return [];
    return programNames.map((program) => ({
      value: program.id,
      label: program.name,
    }));
  }, [programNames]);

  const discountScopeOptions = useMemo(() => {
    if (!discount_scope) return [];
    return discount_scope.map((scope) => ({
      value: scope.id, // Use ID as value
      label: scope.name,
    }));
  }, [discount_scope]);

  const discountStackGroupsOptions = useMemo(() => {
    if (!discount_stack_groups) return [];
    return discount_stack_groups.map((group) => ({
      value: group.id, // Use ID as value
      label: group.name,
    }));
  }, [discount_stack_groups]);

  const formik = useFormik({
    initialValues: {
      name: "",
      program: "",
      numberOfCodes: "",
      channel: "",
      usesPerCode: "",
      startDate: "",
      endDate: "",
      discount_scope: [],
      is_condition_applied: false,
      // Condition fields
      priority: "",
      plan_in: [],
      billing_cycle_in: [],
      min_seats: "",
      tenant_industry_in: [],
      geo_in: [],
      trial_state_in: [],
      min_contract_term_months: "",
      first_purchase_only: false,
      discount_stack_groups: [],
    },
    validate,
    onSubmit: (values) => {
      if (modalMode === "edit") {
        handleConfirmUpdate(values);
      } else {
        handleConfirmGenerate(values);
      }
    },
  });

  // Function to fetch code details by ID
  const fetchCodeDetails = (codeId) => {
    setViewDetailsLoading(true);
    dispatch(
      reqToGetCodeById({
        data: { id: codeId },
        onSuccess: (response) => {
          const codeData = response.result;
          setSelectedCodeData(codeData);

          // Prepopulate form with existing data including conditions
          const formValues = {
            name: codeData.name || "",
            program: codeData.program_id || "",
            numberOfCodes: codeData.number_of_code || "",
            channel: codeData.channel || "",
            usesPerCode: codeData.uses_per_code || "",
            startDate: codeData.start_date
              ? codeData.start_date.split("T")[0]
              : "",
            endDate: codeData.expires_at
              ? codeData.expires_at.split("T")[0]
              : "",
            discount_scope: Array.isArray(codeData.scope)
              ? codeData.scope
              : [codeData.scope],
            is_condition_applied: codeData.is_condition_applied || false,
            // Condition fields
            priority: codeData.conditions?.priority || "",
            plan_in: codeData.conditions?.plan_in || [],
            billing_cycle_in: codeData.conditions?.billing_cycle_in || [],
            min_seats: codeData.conditions?.min_seats || "",
            tenant_industry_in: codeData.conditions?.tenant_industry_in || [],
            geo_in: codeData.conditions?.geo_in || [],
            trial_state_in: codeData.conditions?.trial_state_in || [],
            min_contract_term_months:
              codeData.conditions?.min_contract_term_months || "",
            first_purchase_only:
              codeData.conditions?.first_purchase_only || false,
            discount_stack_groups: codeData.conditions?.staking_group || [],
          };
          formik.setValues(formValues);
          setViewDetailsLoading(false);
        },
        onFailure: (error) => {
          console.error("Error fetching code details:", error);
          setViewDetailsLoading(false);
        },
      })
    );
  };

  const handleDeleteProgram = (row) => {
    dispatch(
      reqToUpdateCodeStatus({
        data: { id: row.id, action: "deleted" },
        onSuccess: (response) => {
          dispatch(
            reqToGetCodeList({
              data: { page: currentPage, limit: pageSize },
              onSuccess: () => setLoading(false),
              onFailure: () => setLoading(false),
            })
          );
        },
        onFailure: (error) => {
          console.error("Error deleting program:", error);
        },
      })
    );
  };

  const handlePauseProgram = (row) => {
    const action = row.status === "active" ? "deactive" : "active";

    dispatch(
      reqToUpdateCodeStatus({
        data: { id: row.id, action: action },
        onSuccess: (response) => {
          dispatch(
            reqToGetCodeList({
              data: { page: currentPage, limit: pageSize },
              onSuccess: () => setLoading(false),
              onFailure: () => setLoading(false),
            })
          );
        },
        onFailure: (error) => {
          console.error("Error updating program status:", error);
        },
      })
    );
  };

  const handleViewDetails = (row) => {
    setModalMode("view");
    setSelectedCodeId(row.id);
    setIsModalOpen(true);
    fetchCodeDetails(row.id);
  };

  const handleEditProgram = (row) => {
    setModalMode("edit");
    setSelectedCodeId(row.id);
    fetchCodeDetails(row.id);
    setIsModalOpen(true);
  };

  const handleGenerateCode = () => {
    setModalMode("create");
    setSelectedCodeId(null);
    setSelectedCodeData(null);
    formik.resetForm();
    setIsModalOpen(true);
  };

  const handleCopyCode = (text, event) => {
    if (event && typeof event.stopPropagation === "function") {
      event.stopPropagation();
    }
    if (!text) return;
    if (navigator && navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard
        .writeText(text)
        .then(() => notify("Code copied to clipboard", "success"))
        .catch(() => notify("Failed to copy code", "error"));
    } else {
      try {
        const textarea = document.createElement("textarea");
        textarea.value = text;
        textarea.setAttribute("readonly", "");
        textarea.style.position = "absolute";
        textarea.style.left = "-9999px";
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand("copy");
        document.body.removeChild(textarea);
        notify("Code copied to clipboard", "success");
      } catch (e) {
        notify("Failed to copy code", "error");
      }
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setModalMode("create");
    setSelectedCodeId(null);
    setSelectedCodeData(null);
    setViewDetailsLoading(false);
    formik.resetForm();
  };

  const handleConfirmGenerate = (values) => {
    const basePayload = {
      name: values.name,
      program_id: values.program,
      number_of_code: values.numberOfCodes,
      channel: values.channel,
      uses_per_code: values.usesPerCode,
      start_date: values.startDate,
      expires_at: values.endDate,
      scope: Array.isArray(values.discount_scope)
        ? values.discount_scope
        : [values.discount_scope],
      is_condition_applied: values.is_condition_applied,
    };

    // Add conditions only if is_condition_applied is true and at least one condition exists
    if (values.is_condition_applied) {
      const conditions = {};

      // Only include condition fields that have values
      if (values.priority) conditions.priority = values.priority;
      if (values.plan_in && values.plan_in.length > 0)
        conditions.plan_in = values.plan_in;
      if (values.billing_cycle_in && values.billing_cycle_in.length > 0)
        conditions.billing_cycle_in = values.billing_cycle_in;
      if (values.min_seats) conditions.min_seats = parseInt(values.min_seats);
      if (values.tenant_industry_in && values.tenant_industry_in.length > 0)
        conditions.tenant_industry_in = values.tenant_industry_in;
      if (values.geo_in && values.geo_in.length > 0)
        conditions.geo_in = values.geo_in;
      if (values.trial_state_in && values.trial_state_in.length > 0)
        conditions.trial_state_in = values.trial_state_in;
      if (values.min_contract_term_months)
        conditions.min_contract_term_months = parseInt(
          values.min_contract_term_months
        );
      if (values.first_purchase_only)
        conditions.first_purchase_only = values.first_purchase_only;
      if (
        values.discount_stack_groups &&
        values.discount_stack_groups.length > 0
      )
        conditions.staking_group = values.discount_stack_groups;

      // Only add conditions object if it has at least one property
      if (Object.keys(conditions).length > 0) {
        basePayload.conditions = conditions;
      }
    }

    dispatch(
      reqToCreateCode({
        data: basePayload,
        onSuccess: (response) => {
          setIsModalOpen(false);
          formik.resetForm();
          formik.setSubmitting(false);
          dispatch(
            reqToGetCodeList({
              data: { page: currentPage, limit: pageSize },
              onSuccess: () => setLoading(false),
              onFailure: () => setLoading(false),
            })
          );
        },
        onFailure: (error) => {
          formik.setSubmitting(false);
          console.error("Error creating program:", error);
        },
      })
    );
  };

  const handleImportCode = () => {
    setImportModalOpen(true);
  };

  const handleExportAllCSV = () => {
    if (!codeList || codeList.length === 0) {
      notify("No data available for export", "error");
      return;
    }

    try {
      exportAllToCSV(codeList, "all-discount-codes.csv");
    } catch (error) {
      notify("Failed to export CSV", "error");
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Check if it's an Excel file
      if (
        file.type ===
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
        file.name.endsWith(".xlsx")
      ) {
        setSelectedFile(file);
      } else {
        notify("Please select a valid Excel file (.xlsx)", "error");
        setSelectedFile(null);
      }
    }
  };

  const handleConfirmImport = () => {
    if (!selectedFile) {
      notify("Please select a file to import", "error");
      return;
    }

    setImportLoading(true);

    // Create FormData for file upload
    const formData = new FormData();
    formData.append("file", selectedFile);

    // Dispatch import action
    dispatch(
      reqToImportCode({
        data: formData,
        onSuccess: (response) => {
          setImportLoading(false);
          setImportModalOpen(false);
          setSelectedFile(null);
          notify("Codes imported successfully", "success");

          // Refresh the code list
          dispatch(
            reqToGetCodeList({
              data: { page: currentPage, limit: pageSize },
              onSuccess: () => setLoading(false),
              onFailure: () => setLoading(false),
            })
          );
        },
        onFailure: (error) => {
          setImportLoading(false);
          console.error("Error importing codes:", error);
          notify("Failed to import codes", "error");
        },
      })
    );
  };

  const handleCloseImportModal = () => {
    setImportModalOpen(false);
    setSelectedFile(null);
    setImportLoading(false);
  };

  const handleConfirmUpdate = (values) => {
    const basePayload = {
      action: "edit",
      name: values.name,
      program_id: values.program,
      number_of_code: values.numberOfCodes,
      channel: values.channel,
      uses_per_code: values.usesPerCode,
      start_date: values.startDate,
      expires_at: values.endDate,
      scope: Array.isArray(values.discount_scope)
        ? values.discount_scope
        : [values.discount_scope],
      is_condition_applied: values.is_condition_applied,
    };

    // Add conditions only if is_condition_applied is true and at least one condition exists
    if (values.is_condition_applied) {
      const conditions = {};

      // Only include condition fields that have values
      if (values.priority) conditions.priority = values.priority;
      if (values.plan_in && values.plan_in.length > 0)
        conditions.plan_in = values.plan_in;
      if (values.billing_cycle_in && values.billing_cycle_in.length > 0)
        conditions.billing_cycle_in = values.billing_cycle_in;
      if (values.min_seats) conditions.min_seats = parseInt(values.min_seats);
      if (values.tenant_industry_in && values.tenant_industry_in.length > 0)
        conditions.tenant_industry_in = values.tenant_industry_in;
      if (values.geo_in && values.geo_in.length > 0)
        conditions.geo_in = values.geo_in;
      if (values.trial_state_in && values.trial_state_in.length > 0)
        conditions.trial_state_in = values.trial_state_in;
      if (values.min_contract_term_months)
        conditions.min_contract_term_months = parseInt(
          values.min_contract_term_months
        );
      if (values.first_purchase_only)
        conditions.first_purchase_only = values.first_purchase_only;
      if (
        values.discount_stack_groups &&
        values.discount_stack_groups.length > 0
      )
        conditions.staking_group = values.discount_stack_groups;

      // Only add conditions object if it has at least one property
      if (Object.keys(conditions).length > 0) {
        basePayload.conditions = conditions;
      }
    }

    dispatch(
      reqToUpdateCode({
        data: basePayload,
        id: selectedCodeId,
        onSuccess: (response) => {
          setIsModalOpen(false);
          formik.resetForm();
          dispatch(
            reqToGetCodeList({
              data: { page: currentPage, limit: pageSize },
              onSuccess: () => setLoading(false),
              onFailure: () => setLoading(false),
            })
          );
        },
        onFailure: (error) => {
          console.error("Error updating program:", error);
        },
      })
    );
  };

  const handlePageSizeChange = (event) => {
    setPageSize(Number(event.target.value));
    setCurrentPage(1);
  };

  const goToPage = (pageNum) => {
    if (pageNum < 1 || pageNum > totalPages) return;
    setCurrentPage(pageNum);
  };

  const customStyles = {
    control: (base, state) => ({
      ...base,
      border: "1px solid #E5E7EB",
      borderRadius: "8px",
      padding: "4px 8px",
      boxShadow: "none",
      "&:hover": {
        borderColor: "#3B82F6",
      },
      backgroundColor: state.isDisabled ? "#F9FAFB" : "#FFFFFF",
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected
        ? "#3B82F6"
        : state.isFocused
        ? "#EFF6FF"
        : "#FFFFFF",
      color: state.isSelected ? "#FFFFFF" : "#374151",
      padding: "8px 12px",
    }),
    menu: (base) => ({
      ...base,
      borderRadius: "8px",
      boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
    }),
    placeholder: (base) => ({
      ...base,
      color: "#9CA3AF",
    }),
  };

  // Define table columns (same as before)
  const columns = [
    {
      key: "code",
      dataIndex: "name",
      title: "Code",
      render: (value, record) => (
        <span className="flex gap-2 p-0 items-center !font-semibold">
          {value}
          <span
            role="button"
            tabIndex={0}
            title="Copy code"
            onClick={(e) => handleCopyCode(value, e)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") handleCopyCode(value, e);
            }}
            className="cursor-pointer"
          >
            {CopyIcon}
          </span>
        </span>
      ),
    },
    {
      key: "program",
      dataIndex: "program",
      title: "Program",
      render: (value, record) => value?.name || "N/A",
    },
    {
      key: "type",
      dataIndex: "type",
      title: "Type",
      render: (value, record) => {
        if (typeof value === "number") {
          const discountType = programVariables?.find(
            (item) => item.id === value
          );
          return discountType ? discountType.name : "N/A";
        }
        return value?.name || value || "N/A";
      },
    },

    {
      key: "usage",
      dataIndex: "used_codes",
      title: "Usage",
      render: (value, record) => {
        const totalCodes = record.number_of_code || 0;
        const usedCodes = value || 0;
        const usagePercentage =
          totalCodes > 0 ? Math.round((usedCodes / totalCodes) * 100) : 0;

        return (
          <>
            {usedCodes}/{totalCodes}
            <div className="flex items-center gap-2 mt-2">
              <div className="w-full h-[5px] bg-gray-100 rounded w-[50px]">
                <div
                  className="h-[5px] bg-[#F45C0C] rounded"
                  style={{ width: `${Math.min(usagePercentage, 100)}%` }}
                ></div>
              </div>
              {usagePercentage}%
            </div>
          </>
        );
      },
    },
    {
      key: "channel",
      dataIndex: "channel",
      title: "Channel",
      render: (value, record) => {
        // Find the channel name from channelOptions based on the ID
        const channelOption = channelOptions.find(
          (option) => option.value === value
        );
        const channelName = channelOption ? channelOption.label : "N/A";

        return <span className="font-semibold">{channelName}</span>;
      },
    },
    {
      key: "dateexpires",
      dataIndex: "expires_at",
      title: "Expires",
      render: (value, record) => (
        <span className="font-semibold">
          {new Date(value).toLocaleDateString()}
        </span>
      ),
    },
    // {
    //   key: "creator",
    //   dataIndex: "user",
    //   title: "Creator",
    //   render: (value, record) => (
    //     <span className="font-semibold">{value?.name || "N/A"}</span>
    //   ),
    // },
    {
      key: "status",
      dataIndex: "status",
      title: "Status",
      render: (value, record) => (
        <span
          className={`rounded-full text-xs font-medium ${
            value === "active"
              ? "bg-[#E9FFDF] text-[#52C41A]"
              : "bg-[#FFEFEE] text-[#EA4335]"
          }`}
        >
          {value?.charAt(0)?.toUpperCase() + value?.slice(1)}
        </span>
      ),
    },
  ];

  // Define row actions (same as before)
  const rowActions = {
    threeDotsIcon: threeDotsIcon,
    actions: (row) => [
      {
        label: "View details",
        icon: ViewDetailsIcon,
        to: "#",
        onClick: (row, index) => {
          handleViewDetails(row);
        },
      },
      {
        label: "Edit program",
        icon: EditProgramIcon,
        to: "#",
        onClick: (row, index) => {
          handleEditProgram(row);
        },
      },
      {
        label: "Manage codes",
        icon: ManageCodesIcon,
        to: "#",
        onClick: (row, index) => {
          handleEditProgram(row);
        },
      },
      {
        label: row.status === "active" ? "Pause Program" : "Activate Program",
        icon: PauseProgramIcon,
        to: "#",
        onClick: (row, index) => {
          handlePauseProgram(row);
        },
      },
      {
        label: "Delete Program",
        icon: DeleteProgramIcon,
        to: "#",
        onClick: (row, index) => {
          handleDeleteProgram(row);
        },
      },
    ],
  };

  const getModalConfig = () => {
    switch (modalMode) {
      case "view":
        return {
          title: "View Code Details",
          confirmText: "Close",
          showCancel: false,
        };
      case "edit":
        return {
          title: "Edit Code",
          confirmText: "Update Code",
          showCancel: true,
        };
      default:
        return {
          title: "Bulk Generate Code",
          confirmText: "Generate Codes",
          showCancel: true,
        };
    }
  };

  const modalConfig = getModalConfig();

  return (
    <div className="">
      <div className="flex gap-4">
        <button className="btn-light-gray" onClick={handleGenerateCode}>
          {BlackAddIcon} Generate code
        </button>
        <button
          className="btn-light-gray"
          onClick={() => navigate(routes.importCsv)}
        >
          {BlackAddIcon} Import Codes
        </button>
        <button className="btn-light-gray" onClick={handleExportAllCSV}>
          {BlackAddIcon} Export CSV
        </button>
        {/* <button className="btn-light-gray">
          {BlackAddIcon} Add New Program
        </button> */}
        {/* <button className="btn-light-gray">
          {BlackAddIcon} Add New Program
        </button> */}
      </div>

      <Table
        columns={columns}
        data={codeList}
        keyField="id"
        rowActions={rowActions}
        openRow={openRow}
        setOpenRow={setOpenRow}
        dropdownRef={dropdownRef}
        emptyMessage="No codes found"
        onRowClick={(row) => {}}
      />

      <PaginationTab
        currentPage={currentPage}
        pageSize={pageSize}
        totalCount={totalCount}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
        onPageSizeChange={setPageSize}
      />

      <PopUpModal
        visible={isModalOpen}
        onClose={handleCloseModal}
        onConfirm={
          modalMode === "view" ? handleCloseModal : formik.handleSubmit
        }
        title={modalConfig.title}
        confirmText={modalConfig.confirmText}
        cancelText={modalConfig.showCancel ? "Close" : undefined}
        size="medium"
        confirmDisabled={
          modalMode === "view" ? false : !formik.isValid || formik.isSubmitting
        }
        hideConfirm={modalMode === "view"}
      >
        {modalMode === "view" && viewDetailsLoading ? (
          <div className="flex justify-center items-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
            <span className="ml-2">Loading details...</span>
          </div>
        ) : (
          <form onSubmit={formik.handleSubmit} className="space-y-6">
            {/* Existing form fields */}
            <div className="form-group">
              <label
                htmlFor="name"
                className="form-label block text-sm font-medium text-gray-700 mb-2"
              >
                Name
              </label>
              <input
                type="text"
                id="name"
                name="name"
                min="1"
                value={formik.values.name}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                disabled={modalMode === "view"}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                placeholder="Enter code name"
              />
              {formik.touched.name && formik.errors.name && (
                <div className="text-red-500 text-xs mt-1">
                  {formik.errors.name}
                </div>
              )}
            </div>

            <div className="form-group">
              <label
                htmlFor="program"
                className="form-label block text-sm font-medium text-gray-700 mb-2"
              >
                Select Program
              </label>
              <Select
                id="program"
                name="program"
                options={programOptions}
                value={programOptions.find(
                  (option) => option.value === formik.values.program
                )}
                onChange={(selectedOption) =>
                  formik.setFieldValue("program", selectedOption?.value || "")
                }
                onBlur={formik.handleBlur}
                placeholder="Choose discount program"
                styles={customStyles}
                isSearchable
                isLoading={!programNames}
                isDisabled={modalMode === "view"}
              />
              {formik.touched.program && formik.errors.program && (
                <div className="text-red-500 text-xs mt-1">
                  {formik.errors.program}
                </div>
              )}
            </div>

            <div className="form-group">
              <label
                htmlFor="numberOfCodes"
                className="form-label block text-sm font-medium text-gray-700 mb-2"
              >
                Number of Codes
              </label>
              <input
                type="number"
                id="numberOfCodes"
                name="numberOfCodes"
                min="1"
                value={formik.values.numberOfCodes}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                disabled={modalMode === "view"}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                placeholder="Enter number of codes"
              />
              {formik.touched.numberOfCodes && formik.errors.numberOfCodes && (
                <div className="text-red-500 text-xs mt-1">
                  {formik.errors.numberOfCodes}
                </div>
              )}
            </div>

            <div className="form-group">
              <label
                htmlFor="channel"
                className="form-label block text-sm font-medium text-gray-700 mb-2"
              >
                Channel
              </label>
              <Select
                id="channel"
                name="channel"
                options={channelOptions}
                value={channelOptions.find(
                  (option) => option.value === formik.values.channel
                )}
                onChange={(selectedOption) =>
                  formik.setFieldValue("channel", selectedOption?.value || "")
                }
                onBlur={formik.handleBlur}
                placeholder="Select channel"
                styles={customStyles}
                isLoading={!discount_channals}
                isDisabled={modalMode === "view"}
              />
              {formik.touched.channel && formik.errors.channel && (
                <div className="text-red-500 text-xs mt-1">
                  {formik.errors.channel}
                </div>
              )}
            </div>

            <div className="form-group">
              <label
                htmlFor="usesPerCode"
                className="form-label block text-sm font-medium text-gray-700 mb-2"
              >
                Uses per code
              </label>
              <Select
                id="usesPerCode"
                name="usesPerCode"
                options={usesPerCodeOptions}
                value={usesPerCodeOptions.find(
                  (option) => option.value === formik.values.usesPerCode
                )}
                onChange={(selectedOption) =>
                  formik.setFieldValue(
                    "usesPerCode",
                    selectedOption?.value || ""
                  )
                }
                onBlur={formik.handleBlur}
                placeholder="Select usage type"
                styles={customStyles}
                isDisabled={modalMode === "view"}
              />
              {formik.touched.usesPerCode && formik.errors.usesPerCode && (
                <div className="text-red-500 text-xs mt-1">
                  {formik.errors.usesPerCode}
                </div>
              )}
            </div>

            <div className="form-group">
              <label
                htmlFor="startDate"
                className="form-label block text-sm font-medium text-gray-700 mb-2"
              >
                Start Date
              </label>
              <DatePicker
                id="startDate"
                name="startDate"
                selected={
                  formik.values.startDate
                    ? new Date(formik.values.startDate)
                    : null
                }
                onChange={(date) =>
                  formik.setFieldValue(
                    "startDate",
                    date ? date.toISOString().split("T")[0] : ""
                  )
                }
                onBlur={formik.handleBlur}
                disabled={modalMode === "view"}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                dateFormat="yyyy-MM-dd"
                placeholderText="Select start date"
              />
              {formik.touched.startDate && formik.errors.startDate && (
                <div className="text-red-500 text-xs mt-1">
                  {formik.errors.startDate}
                </div>
              )}
            </div>

            <div className="form-group">
              <label
                htmlFor="endDate"
                className="form-label block text-sm font-medium text-gray-700 mb-2"
              >
                End Date
              </label>
              <DatePicker
                id="endDate"
                name="endDate"
                selected={
                  formik.values.endDate ? new Date(formik.values.endDate) : null
                }
                onChange={(date) =>
                  formik.setFieldValue(
                    "endDate",
                    date ? date.toISOString().split("T")[0] : ""
                  )
                }
                onBlur={formik.handleBlur}
                disabled={modalMode === "view"}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                dateFormat="yyyy-MM-dd"
                placeholderText="Select end date"
              />
              {formik.touched.endDate && formik.errors.endDate && (
                <div className="text-red-500 text-xs mt-1">
                  {formik.errors.endDate}
                </div>
              )}
            </div>

            <div className="form-group">
              <label
                htmlFor="discount_scope"
                className="form-label block text-sm font-medium text-gray-700 mb-2"
              >
                Discount Scope
              </label>
              <Select
                id="discount_scope"
                name="discount_scope"
                options={discountScopeOptions}
                value={discountScopeOptions.filter((option) =>
                  formik.values.discount_scope.includes(option.value)
                )}
                onChange={(selectedOptions) => {
                  // Ensure we always set an array of values
                  const selectedValues = selectedOptions
                    ? selectedOptions.map((option) => option.value)
                    : [];
                  formik.setFieldValue("discount_scope", selectedValues);
                }}
                onBlur={formik.handleBlur}
                placeholder="Select discount scope"
                styles={customStyles}
                isMulti
                isDisabled={modalMode === "view"}
                closeMenuOnSelect={false} // This allows multiple selections without closing the menu
              />
              {formik.touched.discount_scope &&
                formik.errors.discount_scope && (
                  <div className="text-red-500 text-xs mt-1">
                    {formik.errors.discount_scope}
                  </div>
                )}
            </div>

            {/* Apply Conditions Checkbox */}
            <div className="form-group border-t pt-4">
              <div className="flex space-x-4 items-center mt-4">
                <label
                  htmlFor="is_condition_applied"
                  className="flex items-center cursor-pointer"
                >
                  <input
                    type="checkbox"
                    id="is_condition_applied"
                    name="is_condition_applied"
                    checked={formik.values.is_condition_applied}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    disabled={modalMode === "view"}
                    className="hidden"
                  />

                  <span
                    className={`w-5 h-5 rounded-md border-2 flex items-center justify-center transition-colors ${
                      formik.values.is_condition_applied
                        ? "bg-blue-500 border-blue-500"
                        : "bg-white border-gray-300"
                    }`}
                  >
                    {formik.values.is_condition_applied && (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-4 w-4 text-white"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 00-1.414 0L9 11.586 6.707 9.293a1 1 0 00-1.414 1.414l3 3a1 1 0 001.414 0l7-7a1 1 0 000-1.414z"
                          clipRule="evenodd"
                        />
                      </svg>
                    )}
                  </span>

                  <div className="ml-2 text-sm font-medium text-gray-700">
                    Apply Conditions
                  </div>
                </label>
              </div>
            </div>

            {/* Condition Fields - Only show when checkbox is checked */}
            {formik.values.is_condition_applied && (
              <div className="condition-fields space-y-4 p-4 border border-gray-200 rounded-lg bg-gray-50">
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  Conditions
                </h3>
                {formik.errors.condition_fields && (
                  <div className="text-red-500 text-xs mt-1 bg-red-50 p-2 rounded border border-red-200">
                    {formik.errors.condition_fields}
                  </div>
                )}
                {/* Priority */}
                <div className="form-group">
                  <label
                    htmlFor="priority"
                    className="form-label block text-sm font-medium text-gray-700 mb-2"
                  >
                    Priority
                  </label>
                  <input
                    type="number"
                    id="priority"
                    name="priority"
                    min="1"
                    value={formik.values.priority}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    disabled={modalMode === "view"}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                    placeholder="Enter priority"
                  />
                  {formik.touched.priority && formik.errors.priority && (
                    <div className="text-red-500 text-xs mt-1">
                      {formik.errors.priority}
                    </div>
                  )}
                </div>
                {/* Plan In */}
                <div className="form-group">
                  <label
                    htmlFor="plan_in"
                    className="form-label block text-sm font-medium text-gray-700 mb-2"
                  >
                    Plans
                  </label>
                  <Select
                    id="plan_in"
                    name="plan_in"
                    options={planOptions}
                    value={planOptions.filter((option) =>
                      formik.values.plan_in.includes(option.value)
                    )}
                    onChange={(selectedOptions) =>
                      formik.setFieldValue(
                        "plan_in",
                        selectedOptions
                          ? selectedOptions.map((option) => option.value)
                          : []
                      )
                    }
                    onBlur={formik.handleBlur}
                    placeholder="Select plans"
                    styles={customStyles}
                    isMulti
                    isDisabled={modalMode === "view"}
                  />
                  {formik.touched.plan_in && formik.errors.plan_in && (
                    <div className="text-red-500 text-xs mt-1">
                      {formik.errors.plan_in}
                    </div>
                  )}
                </div>
                {/* Billing Cycle In */}
                <div className="form-group">
                  <label
                    htmlFor="billing_cycle_in"
                    className="form-label block text-sm font-medium text-gray-700 mb-2"
                  >
                    Billing Cycles
                  </label>
                  <Select
                    id="billing_cycle_in"
                    name="billing_cycle_in"
                    options={billingCycleOptions}
                    value={billingCycleOptions.filter((option) =>
                      formik.values.billing_cycle_in.includes(option.value)
                    )}
                    onChange={(selectedOptions) =>
                      formik.setFieldValue(
                        "billing_cycle_in",
                        selectedOptions
                          ? selectedOptions.map((option) => option.value)
                          : []
                      )
                    }
                    onBlur={formik.handleBlur}
                    placeholder="Select billing cycles"
                    styles={customStyles}
                    isMulti
                    isDisabled={modalMode === "view"}
                  />
                  {formik.touched.billing_cycle_in &&
                    formik.errors.billing_cycle_in && (
                      <div className="text-red-500 text-xs mt-1">
                        {formik.errors.billing_cycle_in}
                      </div>
                    )}
                </div>
                {/* Min Seats */}
                <div className="form-group">
                  <label
                    htmlFor="min_seats"
                    className="form-label block text-sm font-medium text-gray-700 mb-2"
                  >
                    Minimum Seats
                  </label>
                  <input
                    type="number"
                    id="min_seats"
                    name="min_seats"
                    min="1"
                    value={formik.values.min_seats}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    disabled={modalMode === "view"}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                    placeholder="Enter minimum seats"
                  />
                  {formik.touched.min_seats && formik.errors.min_seats && (
                    <div className="text-red-500 text-xs mt-1">
                      {formik.errors.min_seats}
                    </div>
                  )}
                </div>
                {/* Tenant Industry In */}
                <div className="form-group">
                  <label
                    htmlFor="tenant_industry_in"
                    className="form-label block text-sm font-medium text-gray-700 mb-2"
                  >
                    Tenant Industries
                  </label>
                  <Select
                    id="tenant_industry_in"
                    name="tenant_industry_in"
                    options={tenantIndustryOptions}
                    value={tenantIndustryOptions.filter((option) =>
                      formik.values.tenant_industry_in.includes(option.value)
                    )}
                    onChange={(selectedOptions) =>
                      formik.setFieldValue(
                        "tenant_industry_in",
                        selectedOptions
                          ? selectedOptions.map((option) => option.value)
                          : []
                      )
                    }
                    onBlur={formik.handleBlur}
                    placeholder="Select tenant industries"
                    styles={customStyles}
                    isMulti
                    isDisabled={modalMode === "view"}
                  />
                  {formik.touched.tenant_industry_in &&
                    formik.errors.tenant_industry_in && (
                      <div className="text-red-500 text-xs mt-1">
                        {formik.errors.tenant_industry_in}
                      </div>
                    )}
                </div>
                {/* Geo In */}
                <div className="form-group">
                  <label
                    htmlFor="geo_in"
                    className="form-label block text-sm font-medium text-gray-700 mb-2"
                  >
                    Geographies
                  </label>
                  <Select
                    id="geo_in"
                    name="geo_in"
                    options={geoOptions}
                    value={geoOptions.filter((option) =>
                      formik.values.geo_in.includes(option.value)
                    )}
                    onChange={(selectedOptions) =>
                      formik.setFieldValue(
                        "geo_in",
                        selectedOptions
                          ? selectedOptions.map((option) => option.value)
                          : []
                      )
                    }
                    onBlur={formik.handleBlur}
                    placeholder="Select geographies"
                    styles={customStyles}
                    isMulti
                    isDisabled={modalMode === "view"}
                  />
                  {formik.touched.geo_in && formik.errors.geo_in && (
                    <div className="text-red-500 text-xs mt-1">
                      {formik.errors.geo_in}
                    </div>
                  )}
                </div>
                {/* Trial State In */}
                <div className="form-group">
                  <label
                    htmlFor="trial_state_in"
                    className="form-label block text-sm font-medium text-gray-700 mb-2"
                  >
                    Trial States
                  </label>
                  <Select
                    id="trial_state_in"
                    name="trial_state_in"
                    options={trialStateOptions}
                    value={trialStateOptions.filter((option) =>
                      formik.values.trial_state_in.includes(option.value)
                    )}
                    onChange={(selectedOptions) =>
                      formik.setFieldValue(
                        "trial_state_in",
                        selectedOptions
                          ? selectedOptions.map((option) => option.value)
                          : []
                      )
                    }
                    onBlur={formik.handleBlur}
                    placeholder="Select trial states"
                    styles={customStyles}
                    isMulti
                    isDisabled={modalMode === "view"}
                  />
                  {formik.touched.trial_state_in &&
                    formik.errors.trial_state_in && (
                      <div className="text-red-500 text-xs mt-1">
                        {formik.errors.trial_state_in}
                      </div>
                    )}
                </div>
                {/* Min Contract Term Months */}
                <div className="form-group">
                  <label
                    htmlFor="min_contract_term_months"
                    className="form-label block text-sm font-medium text-gray-700 mb-2"
                  >
                    Minimum Contract Term (Months)
                  </label>
                  <input
                    type="number"
                    id="min_contract_term_months"
                    name="min_contract_term_months"
                    min="1"
                    value={formik.values.min_contract_term_months}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    disabled={modalMode === "view"}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                    placeholder="Enter minimum contract term"
                  />
                  {formik.touched.min_contract_term_months &&
                    formik.errors.min_contract_term_months && (
                      <div className="text-red-500 text-xs mt-1">
                        {formik.errors.min_contract_term_months}
                      </div>
                    )}
                </div>

                {/* Discount Stack Groups */}
                <div className="form-group">
                  <label
                    htmlFor="discount_stack_groups"
                    className="form-label block text-sm font-medium text-gray-700 mb-2"
                  >
                    Discount Stack Groups
                  </label>
                  <Select
                    id="discount_stack_groups"
                    name="discount_stack_groups"
                    options={discountStackGroupsOptions}
                    value={discountStackGroupsOptions.filter((option) =>
                      formik.values.discount_stack_groups.includes(option.value)
                    )}
                    onChange={(selectedOptions) =>
                      formik.setFieldValue(
                        "discount_stack_groups",
                        selectedOptions
                          ? selectedOptions.map((option) => option.value)
                          : []
                      )
                    }
                    onBlur={formik.handleBlur}
                    placeholder="Select discount stack groups"
                    styles={customStyles}
                    isMulti
                    isDisabled={modalMode === "view"}
                  />
                  {formik.touched.discount_stack_groups &&
                    formik.errors.discount_stack_groups && (
                      <div className="text-red-500 text-xs mt-1">
                        {formik.errors.discount_stack_groups}
                      </div>
                    )}
                </div>

                {/* First Purchase Only */}
                <div className="form-group">
                  <div className="flex space-x-4 items-center mt-2">
                    <label
                      htmlFor="first_purchase_only"
                      className="flex items-center cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        id="first_purchase_only"
                        name="first_purchase_only"
                        checked={formik.values.first_purchase_only}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        disabled={modalMode === "view"}
                        className="hidden"
                      />

                      <span
                        className={`w-5 h-5 rounded-md border-2 flex items-center justify-center transition-colors ${
                          formik.values.first_purchase_only
                            ? "bg-blue-500 border-blue-500"
                            : "bg-white border-gray-300"
                        }`}
                      >
                        {formik.values.first_purchase_only && (
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-4 w-4 text-white"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                          >
                            <path
                              fillRule="evenodd"
                              d="M16.707 5.293a1 1 0 00-1.414 0L9 11.586 6.707 9.293a1 1 0 00-1.414 1.414l3 3a1 1 0 001.414 0l7-7a1 1 0 000-1.414z"
                              clipRule="evenodd"
                            />
                          </svg>
                        )}
                      </span>

                      <div className="ml-2 text-sm font-medium text-gray-700">
                        First Purchase Only
                      </div>
                    </label>
                  </div>
                </div>
              </div>
            )}
          </form>
        )}
      </PopUpModal>

      {/* Import Codes Modal */}
      <PopUpModal
        visible={importModalOpen}
        onClose={handleCloseImportModal}
        onConfirm={handleConfirmImport}
        title="Import Codes"
        confirmText={importLoading ? "Importing..." : "Import Codes"}
        cancelText="Cancel"
        size="small"
        confirmDisabled={!selectedFile || importLoading}
      >
        <div className="space-y-4">
          <div className="text-sm text-gray-600 mb-4">
            Upload an Excel file (.xlsx) with discount codes. The file should
            contain the following columns:
            <ul className="list-disc list-inside mt-2 text-xs">
              <li>Program ID</li>
              <li>User ID</li>
              <li>Name</li>
              <li>Number of Codes</li>
              <li>Uses Per Code</li>
              <li>Type</li>
              <li>Channel</li>
              <li>Start Date</li>
              <li>Expires At</li>
              <li>Scope</li>
              <li>Status</li>
              <li>Is Abuse</li>
              <li>Is Condition Applied</li>
              <li>Conditions</li>
            </ul>
          </div>

          <div className="form-group">
            <label
              htmlFor="file-upload"
              className="form-label block text-sm font-medium text-gray-700 mb-2"
            >
              Select Excel File
            </label>
            <input
              type="file"
              id="file-upload"
              accept=".xlsx, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              onChange={handleFileSelect}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            {selectedFile && (
              <div className="mt-2 text-sm text-green-600">
                Selected: {selectedFile.name}
              </div>
            )}
          </div>

          {importLoading && (
            <div className="flex items-center justify-center py-2">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
              <span className="ml-2 text-sm">Importing codes...</span>
            </div>
          )}
        </div>
      </PopUpModal>
    </div>
  );
};

export default CodesTab;
